import React, { useEffect, useState, useReducer, useMemo } from 'react'
import { Segment, Header, Icon, Form, Input, Button, Table, Modal, Breadcrumb } from 'semantic-ui-react'
import request from 'services/http'
import { forkJoin } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import produce from 'immer'
import { dateIdToFormat, numFormat, densidade20cHyC } from '@rededompedro/utils'
import { useSubscription } from 'utils'
import './Drenagem.scss'

const initialFormValues = {
    densidade: { text: '', value: null },
    densidade_20_calc: { text: '', value: null },
    temperatura: { text: '', value: null },
    inicio: { text: '', value: null },
    fim: { text: '', value: null },
    tanque_id: { text: '', value: null },
    qtd_comb_drenada: { text: '', value: null },
    contem_impurezas: null,
    drenou_tudo: null,
    observacoes: { text: '', value: null }
}

function formReducer(state, action) {
    if ('reset' in action) {
        if (action.reset) {
            return initialFormValues
        }
    }

    if (!(action.field in state)) {
        return state
    }
    return { ...state, [action.field]: action.payload }
}

function getFormValues(form) {
    let data = {}
    for (let fv in form) {
        data[fv] = Object.prototype.toString.call(form[fv]) === '[object Object]' ? form[fv].value : form[fv]
    }
    return data
}

function getFormTexts(form) {
    let data = {}
    for (let fv in form) {
        data[fv] = Object.prototype.toString.call(form[fv]) === '[object Object]' ? form[fv].text : form[fv]
    }
    return data
}

function DrenagemLancamento(props) {

    const [tanques, setTanques] = useState([])
    const [formValues, setFormState] = useReducer(formReducer, initialFormValues)

    const [lancamentos, setLancamentos] = useState({ data: [], loading: false })
    const [showModalCongrats, setModalCongrats] = useState(false)
    const [drainageFinished, setDrainageFinished] = useState(false)

    const getLancamentos$ = useMemo(() => request({
        method: 'GET',
        url: '/combustivel/drenagem/lancamentos',
        params: { lancamentoId: props.match.params.hashid }
    })
        .pipe(map(({ response }) => response.lancamentos))
        , [props.match.params.hashid])

    const getTanques$ = useMemo(() => request({
        method: 'GET',
        url: '/combustivel/tanques'
    })
        .pipe(map(({ response }) => response.tanques))
        , [])

    const start$ = useMemo(() => forkJoin({ tanques: getTanques$, lancamentos: getLancamentos$ }).pipe(
        tap(data => {
            let lancamentosData = []
            if (data.lancamentos.length) {
                lancamentosData = data.lancamentos.map(d => ({
                    ...d,
                    densidade_20_calc: numFormat(d.densidade_20_calc, 4),
                    inicio: dateIdToFormat(d.inicio, 'HH:mm'),
                    fim: dateIdToFormat(d.fim, 'HH:mm')
                }))
            }
            const tanquesAlreadyRelaseds = data.lancamentos.map(l => l.deposito_id)
            setTanques(data.tanques.filter(t => tanquesAlreadyRelaseds.indexOf(t.id) === -1))
            setLancamentos({ loading: false, data: lancamentosData })
        })
    ), [getLancamentos$, getTanques$])
    useSubscription(start$)

    const addLancamento$ = useMemo(() => {
        const values = getFormValues(formValues)
        return request({
            method: 'PUT', url: '/combustivel/drenagem/add-lancamento',
            body: { ...values, drenagem_id: props.match.params.hashid }
        })
            .pipe(
                tap(() => {
                    const data = produce(lancamentos.data, draft => {
                        draft.push(getFormTexts(formValues))
                    })
                    setLancamentos({ data, loading: false })

                    const tanquesData = produce(tanques, draft => {
                        draft.splice(draft.findIndex(t => t.id === values.tanque_id), 1)
                    })

                    setFormState({ reset: true })
                    setTanques(tanquesData)
                })
            )
    }, [formValues, lancamentos, props.match.params.hashid, tanques])

    useSubscription(addLancamento$, lancamentos.loading)

    function handleFieldChange(_, { name, value, options }) {
        switch (name) {
            case 'inicio':
            case 'fim':
                setFormState({
                    field: name,
                    payload: {
                        text: value.replace(/\D/g, '').replace(/(\d{2})(?=(\d))/, '$1:'),
                        value: value.trim() === '' ? null : value
                    }
                })
                break;

            case 'densidade':
            case 'densidade_20_calc':
                value = '' + value.replace(/\D/g, '')
                setFormState({
                    field: name,
                    payload: {
                        text: value.replace(/(\d)(?=(\d))/, '$1,'),
                        value: parseInt(value) / Math.pow(10, value.length - 1)
                    }
                })
                break;

            case 'tanque_id':
                setFormState({
                    field: name,
                    payload: {
                        text: options.find(x => x.value === value)['text'],
                        value: value
                    }
                })
                break;

            case 'temperatura':
                value = '' + value.replace(/\D/g, '')
                setFormState({
                    field: 'temperatura',
                    payload: {
                        text: value.replace(/(\d{2})(?=(\d))/, '$1,'),
                        value: parseInt(value) / Math.pow(10, value.length - 2)
                    }
                })
                break;

            default:
                if (Object.prototype.toString.call(value) === '[object Object]') {
                    setFormState({ field: name, payload: { text: value, value: value.trim() === '' ? null : value } })
                } else {
                    setFormState({ field: name, payload: value })
                }
        }
    }
    
    useEffect(() => {
        if (formValues.densidade.value && formValues.temperatura.value) {
            let data = densidade20cHyC(formValues.densidade.value, formValues.temperatura.value, 'odmb')
            handleFieldChange(null, { name: 'densidade_20_calc', value: data })
        } else {
            setFormState({ field: 'densidade_20_calc', payload: { text: '', value: null } })
        }
    }, [formValues.densidade, formValues.temperatura])

    const finishDrainage$ = useMemo(() =>
        request({ method: 'POST', url: '/combustivel/drenagem/finish', body: { drenagemId: props.match.params.hashid } }).pipe(
            tap(() => setModalCongrats(true))
        )
        , [props.match.params.hashid])
    useSubscription(finishDrainage$, drainageFinished)

    return (
        <>
            <Modal size='tiny' open={showModalCongrats} dimmer='blurring'>
                <Modal.Header><Icon name='smile outline' /> Parabéns!</Modal.Header>
                <Modal.Content>
                    <p style={{ fontSize: 18 }}>Você finalizou mais um processo de drenagem, garantindo a qualidade do combustível de sua unidade.</p>
                </Modal.Content>
                <Modal.Actions style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        positive
                        icon='thumbs up'
                        labelPosition='right'
                        content='Obrigado'
                        onClick={() => props.history.push('/combustivel/drenagem')}
                    />
                </Modal.Actions>
            </Modal>
            <div id="combustivel-drenagem-lancamento" className='style-2'>
                <Breadcrumb size='mini' icon='right angle' sections={[
                    { key: 'home', content: 'Home', link: true },
                    { key: 'combustivel', content: 'Combustível', link: true },
                    { key: 'drenagem', content: 'Drenagem', active: true },
                    { key: 'hashid', content: props.match.params.hashid, active: true }
                ]} />

                <div id="content">
                    <div className="wrapper">
                        <div className="left">
                            <Header size='medium' as='h2'>
                                <Icon name='wpforms' />
                                <Header.Content>Formulário de Lançamento
                                    <Header.Subheader style={{ fontSize: 15 }}>Drenagem</Header.Subheader>
                                </Header.Content>
                            </Header>

                            <Form>
                                <Form.Group>
                                    <Form.Select label='Tanque' width='8' placeholder='Selecione...' name='tanque_id' onChange={handleFieldChange} required options={tanques.map((t, i) => ({
                                        key: i, value: t.id, text: t.tanque
                                    }))} />

                                    <Form.Input
                                        required
                                        name='inicio'
                                        label='Início (hora)'
                                        autoComplete="off"
                                        width='3'
                                        maxLength={5}
                                        value={formValues.inicio.text}
                                        onChange={handleFieldChange}
                                    />
                                    <Form.Input
                                        required
                                        name='fim'
                                        label='Fim (hora)'
                                        autoComplete="off"
                                        width='3'
                                        maxLength={5}
                                        value={formValues.fim.text}
                                        onChange={handleFieldChange}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Field required width='5'>
                                        <label>Qtd. Comb. Drenada</label>
                                        <Input
                                            required
                                            label='ML'
                                            labelPosition='right'
                                            name='qtd_comb_drenada'
                                            value={formValues.qtd_comb_drenada.text}
                                            onChange={handleFieldChange}
                                        />
                                    </Form.Field>

                                    <Form.Field required>
                                        <label>Contém Inpurezas?</label>
                                        <Form.Radio
                                            label='Sim'
                                            name='contem_impurezas'
                                            value={1}
                                            checked={formValues.contem_impurezas === 1}
                                            onChange={handleFieldChange}
                                        />
                                        <Form.Radio
                                            label='Não'
                                            name='contem_impurezas'
                                            value={0}
                                            checked={formValues.contem_impurezas === 0}
                                            onChange={handleFieldChange}
                                        />
                                    </Form.Field>

                                    <Form.Field required>
                                        <label>Drenou tudo até ficar sem impurezas?</label>
                                        <Form.Radio
                                            label='Sim'
                                            name='drenou_tudo'
                                            value={1}
                                            checked={formValues.drenou_tudo === 1}
                                            onChange={handleFieldChange}
                                        />
                                        <Form.Radio
                                            label='Não'
                                            name='drenou_tudo'
                                            value={0}
                                            checked={formValues.drenou_tudo === 0}
                                            onChange={handleFieldChange}
                                        />
                                    </Form.Field>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Input
                                        required
                                        width={4}
                                        name='densidade'
                                        label='Densidade'
                                        autoComplete="off"
                                        maxLength={5}
                                        value={formValues.densidade.text}
                                        onChange={handleFieldChange}
                                    />
                                    <Form.Field required width='4'>
                                        <label>Temperatura</label>
                                        <Input
                                            required
                                            name='temperatura'
                                            label='ºC'
                                            labelPosition='right'
                                            autoComplete="off"
                                            maxLength={5}
                                            value={formValues.temperatura.text}
                                            onChange={handleFieldChange}
                                        />
                                    </Form.Field>
                                    <Form.Input width={4} label='Densidade a 20ºC' value={formValues.densidade_20_calc.text} disabled readOnly />
                                </Form.Group>
                                <Form.TextArea
                                    label='Observações'
                                    name='observacoes'
                                    placeholder='Acontecimentos no processo, imprevistos, informações extras...'
                                    value={formValues.observacoes.text}
                                    onChange={handleFieldChange}
                                />
                                {tanques.length > 0 ? (
                                    <Button primary icon labelPosition='right' onClick={() => setLancamentos(prev => ({ ...prev, loading: true }))} loading={lancamentos.loading} disabled={lancamentos.loading}>
                                        Lançar <Icon name='right arrow' />
                                    </Button>
                                ) : (
                                        <Button positive icon labelPosition='right' onClick={() => setDrainageFinished(true)}>
                                            Finalizar Drenagem <Icon name='check' />
                                        </Button>
                                    )}
                            </Form>
                        </div>
                        <div className="vertical-divider"></div>
                        <div className="right">
                            <Header size='medium' as='h2'>
                                <Icon name='rocket' />
                                <Header.Content>Drenagens lançadas
                                    <Header.Subheader style={{ fontSize: 15 }}>Lançamentos</Header.Subheader>
                                </Header.Content>
                            </Header>
                            {lancamentos.data.length > 0 ? (
                                <Table celled style={{ fontSize: 16 }}>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell>Tanque</Table.HeaderCell>
                                            <Table.HeaderCell>Início</Table.HeaderCell>
                                            <Table.HeaderCell>Fim</Table.HeaderCell>
                                            <Table.HeaderCell>Densidade 20º</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>

                                    <Table.Body>
                                        {lancamentos.data.map((x, i) => (
                                            <Table.Row key={i}>
                                                <Table.Cell>{x.tanque_id}</Table.Cell>
                                                <Table.Cell>{x.inicio}</Table.Cell>
                                                <Table.Cell>{x.fim}</Table.Cell>
                                                <Table.Cell>{x.densidade_20_calc}</Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            ) : (
                                    <Segment placeholder>
                                        <Header icon style={{ fontWeight: 100 }}>
                                            <Icon name='inbox' />
                                            Ainda não há dados para exibir<br />Faça os lançamentos no formulário a esquerda
                                </Header>
                                    </Segment>
                                )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default DrenagemLancamento
